export interface AIColumn {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface AIStat {
  value: string;
  label: string;
}

export interface AIAnswerBlockProps {
  badge?: string;
  title: string;
  headline: string;
  columns: AIColumn[];
  stats?: AIStat[];
  sidebarTitle?: string;
}

export default function AIAnswerBlock({ badge, title, headline, columns, stats, sidebarTitle = 'Why Brooklyn Chooses Us' }: AIAnswerBlockProps) {
  return (
    <section className="bg-cream py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-14">
          {badge && (
            <p className="text-bronze text-xs font-medium uppercase tracking-widest mb-3">{badge}</p>
          )}
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-charcoal mb-3 max-w-3xl leading-tight">
            {title}
          </h2>
          <p className="text-gray-500 text-base max-w-2xl">{headline}</p>
          <div className="w-16 h-0.5 bg-bronze mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Content columns */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
            {columns.map((col, i) => (
              <div key={i}>
                <h3 className="font-serif text-xl font-semibold text-charcoal mb-3">{col.heading}</h3>
                {col.paragraphs.map((p, j) => (
                  <p key={j} className="text-gray-600 text-sm leading-relaxed mb-3">{p}</p>
                ))}
                {col.bullets && col.bullets.length > 0 && (
                  <ul className="space-y-2 mt-3">
                    {col.bullets.map((b, k) => (
                      <li key={k} className="flex items-start gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                          <span className="block w-1.5 h-1.5 bg-bronze rounded-full"></span>
                        </div>
                        <strong className="font-medium text-charcoal">{b}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Stats sidebar */}
          {stats && stats.length > 0 && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-8 border-l-4 border-bronze">
                <h4 className="font-serif text-lg font-semibold text-charcoal mb-6">{sidebarTitle}</h4>
                <div className="space-y-6">
                  {stats.map((stat, i) => (
                    <div key={i} className="border-b border-warmgray pb-5 last:border-0 last:pb-0">
                      <p className="font-serif text-4xl font-bold text-bronze">{stat.value}</p>
                      <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
